import { Button, Menu, Select, Modal, Input } from 'antd';
import ApiService from '../../../core/service/api-service';
import { useModal } from '../../../core/context/ModalContext';
import { useDataGrid } from '../../../core/components/data-grid/DataGridContext';
import { useTranslation } from 'react-i18next';
import RowMenu from '../../../core/components/data-grid/RowMenu';
import { PERMISSIONS, useUser } from '../../../core/context/UserContext';
import StudyAddRequest from '../study-add-request/StudyAddRequest';
import StudyReadUsersModal from '../StudyReadUsersModal';
import StudyUserAssignModal from '../StudyUserAssignModal';
import StudyEditMetadataModal from '../StudyEditMetadataModal';
import PatientEditModal from '../PatientEditModal';
import { Pages } from '../../../AppRouter';
import StudyTypeEnum from '../../../enums/StudyTypeEnum';
import StudyHelper from '../StudyHelper';
import ReportModal from '../../../pages/report/ReportModal';
import ShareEmailModal from '../ShareEmailModal';
import { string } from 'prop-types';
import moment from 'moment/moment';
import StudyShareModal from '../StudyShareModal';
import ReportMissingDicomsModal from '../ReportMissingDicomsModal';
import AddToListModal from '../../study-list/AddToListModal';
import StudySetPriorityModal from '../StudySetPriorityModal';
import DicomZipDownloadModal from '../DicomZipDownloadModal';
import { confirmPermanentDelete } from '../StudyPermanentDeleteConfirm';
import {
    AudioOutlined,
    DeleteFilled,
    DeleteOutlined,
    DownloadOutlined,
    FileAddOutlined,
    SnippetsOutlined,
    StarOutlined,
    ThunderboltOutlined,
    UserAddOutlined,
} from '@ant-design/icons';

export default function StudyRowMenu({ row }) {
    const { t } = useTranslation();
    const { openModal, closeModal } = useModal();
    const { refreshDataGrid, renderExpandedRow } = useDataGrid();
    const { hasPermission, user } = useUser();

    const addRequest = async () => {
        openModal(
            t('Add Request & Anamnez'),
            <StudyAddRequest
                studyRow={row}
                onSaved={async () => {
                    refreshDataGrid();
                    renderExpandedRow(row.id);
                    closeModal();
                }}
                studyId={row.id}
            />
        );
    };

    // Çekim id'sine tıklanınca açılan menüden: listeye ekleme/çıkarma + açıklama.
    // Grid, kapanışta değil DEĞİŞİKLİKTE yenilenir ki yıldız ve aktif liste filtresi anında yansısın.
    const addToList = async () => {
        openModal(
            t('Add to List'),
            <AddToListModal
                studyId={row.id}
                onChanged={() => refreshDataGrid()}
                onClose={() => closeModal()}
            />
        );
    };

    const setStudyType = async (type) => {
        const response = await ApiService.StudyDetails.SetStudyType({
            studyIdList: [row.id],
            studyType: type,
        });

        if (response.success) {
            refreshDataGrid();
        }
    }

    const deleteReadUser = async () => {
        openModal(
            t('Study Read User'),
            <StudyReadUsersModal
                studyIdList={[row.id]}
                onSelected={async (userId) => {
                    const response =
                        await ApiService.StudyDetails.DeleteReadUser(
                            [row.id],
                            userId
                        );
                    if (response.success) {
                        refreshDataGrid();
                        closeModal();
                    }
                }}
            />
        );
    };

    const setReadTrue = async () => {
        const read = async (userId) => {
            const response = await ApiService.StudyDetails.AddRead({
                studyIdList: [row.id],
                isRead: true,
                readUserId: userId,
            });

            if (response.success) {
                refreshDataGrid();
                return true;
            }

            return false;
        };

        if (hasPermission(PERMISSIONS.STUDY_SET_READ_USER)) {
            openModal(
                t('Doctors'),
                <StudyUserAssignModal
                    selectDoctor={true}
                    autoSelect={true}
                    onSelected={async ({ doctor, reporter }) => {
                        if (await read(doctor)) closeModal();
                    }}
                />
            );
        } else {
            if (await read(user?.data?.id)) closeModal();
        }
    };

    const downloadVoiceRecords = async () => {
        const response = await ApiService.VoiceRecord.CreateZip([row.id]);
        if (response.success) {
            ApiService.downloadFile(response.data, response.fileName);
        }
    };

    const deleteStudy = async () => {
        const response = await ApiService.Study.DeleteStudies([row.id]);
        if (response.success) {
            await refreshDataGrid();
        }
    };

    const deleteStudiesPermanent = async () => {
        confirmPermanentDelete({
            t,
            studyIdList: [row.id],
            onDeleted: refreshDataGrid,
        });
    };

    const restoreStudy = async () => {
        const response = await ApiService.Study.RestoreStudies([row.id]);
        if (response.success) {
            await refreshDataGrid();
        }
    };

    let downloadDicomZip = async () => {
        openModal(
            t('Download Dicom Zip'),
            <DicomZipDownloadModal
                studies={[{
                    studyId: row.id,
                    patientName: row.patientName,
                }]}
                onClose={() => closeModal()}
            />
        );
    };

    const openReqReportPage = () => {
        if (StudyHelper.openReqRepPage(null, row)) {
            openModal(
                'Report',
                <ReportModal studyId={row.id} onRefreshGrid={refreshDataGrid} />,
                () => {
                    // Modalda rapor yazılıp onaylanınca study durumu/rengi değişir:
                    // kapanışta grid'i ve (açıksa) satırın detay kartını tazele.
                    refreshDataGrid();
                    renderExpandedRow(row.id);
                },
                window.innerWidth * 0.98,
                20
            );
        }
    };

    const makeKos = async () => {
        const res = await ApiService.Teletip.MakeKos([{ studyId: row.id }]);
        if (res.success) {
        }
    };

    const editStudyMetadata = () => {
        openModal(
            t('Edit Study Metadata'),
            <StudyEditMetadataModal
                studyRow={row}
                onSaved={async () => {
                    refreshDataGrid();
                    closeModal();
                }}
            />
        );
    };

    const editPatient = () => {
        openModal(
            t('Edit Patient'),
            <PatientEditModal
                studyRow={row}
                onSaved={async () => {
                    refreshDataGrid();
                    closeModal();
                }}
            />
        );
    };

    const setReportable = () => {
        let description = '';
        Modal.confirm({
            title: t('Set Reportable'),
            content: (
                <div>
                    <p>{t('Enter a description for setting this study as reportable')}:</p>
                    <Input.TextArea 
                        rows={3}
                        onChange={(e) => description = e.target.value}
                        placeholder={t('Description')}
                    />
                </div>
            ),
            okText: t('Set Reportable'),
            cancelText: t('Cancel'),
            onOk: async () => {
                const response = await ApiService.StudyDetails.SetReportable({
                    studyIdList: [row.id],
                    isReportable: true,
                    description: description
                });
                if (response.success) {
                    refreshDataGrid();
                }
            }
        });
    };

    const setNotReportable = () => {
        let description = '';
        Modal.confirm({
            title: t('Set Not Reportable'),
            content: (
                <div>
                    <p>{t('Enter a description for setting this study as not reportable')}:</p>
                    <Input.TextArea 
                        rows={3}
                        onChange={(e) => description = e.target.value}
                        placeholder={t('Description')}
                    />
                </div>
            ),
            okText: t('Set Not Reportable'),
            cancelText: t('Cancel'),
            onOk: async () => {
                const response = await ApiService.StudyDetails.SetReportable({
                    studyIdList: [row.id],
                    isReportable: false,
                    description: description
                });
                if (response.success) {
                    refreshDataGrid();
                }
            }
        });
    };

    const assignUser = () => {
        openModal(
            t('Assign User'),
            <StudyUserAssignModal
                autoSelect={false}
                selectDoctor={hasPermission(
                    PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER
                )}
                selectReporter={hasPermission(
                    PERMISSIONS.STUDY_ASSIGN_REPORTER_USER
                )}
                onSelected={async ({ doctor, reporter }) => {
                    let resDoctor = null;
                    let resReporter = null;

                    if (hasPermission(PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER)) {
                        resDoctor = await ApiService.StudyUser.AssignDoctorUser({
                            studyIdList: [row.id],
                            doctorUserId: doctor,
                        });
                    }

                    if (hasPermission(PERMISSIONS.STUDY_ASSIGN_REPORTER_USER)) {
                        resReporter =
                            await ApiService.StudyUser.AssignReporterUser({
                                studyIdList: [row.id],
                                reporterUserId: reporter,
                            });
                    }

                    if (resDoctor?.success || resReporter?.success) {
                        refreshDataGrid();
                        closeModal();
                    }
                }}
            />
        );
    };

    const removeDoctorUser = async () => {
        const res = await ApiService.StudyUser.RemoveDoctorUser({
            studyIdList: [row.id],
            doctorUserId: 'remove',
            reporterUserId: null,
        });

        if (res.success) refreshDataGrid();
    };

    const removeReporterUser = async () => {
        const res = await ApiService.StudyUser.RemoveReporterUser({
            studyIdList: [row.id],
            doctorUserId: null,
            reporterUserId: 'remove',
        });

        if (res.success) refreshDataGrid();
    };

    // Acil öncelik: rapor süresi (study_detail.timeout_date) parametrik dakika kadar öne çekilir.
    const setPriority = () => {
        openModal(
            t('Set Priority'),
            <StudySetPriorityModal
                studyRow={row}
                onSaved={async () => {
                    refreshDataGrid();
                    renderExpandedRow(row.id);
                    closeModal();
                }}
                onCancel={() => closeModal()}
            />
        );
    };

    // Okundu / Atamayı Kaldır satırları iki butonlu; ikisi de yoksa çevrelerindeki
    // ayırıcılar da çizilmesin diye önceden hesaplanıyor.
    const canToggleRead =
        hasPermission(PERMISSIONS.STUDY_SET_READ) ||
        hasPermission(PERMISSIONS.STUDY_REMOVE_READ);
    const canRemoveAssignment =
        hasPermission(PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER) ||
        hasPermission(PERMISSIONS.STUDY_ASSIGN_REPORTER_USER);
    const showActionRows = canToggleRead || canRemoveAssignment;

    const canOpenStudySubMenu =
        hasPermission(PERMISSIONS.STUDY_SET_PRIORITY) ||
        hasPermission(PERMISSIONS.LIST_ITEM_ADD) ||
        hasPermission(PERMISSIONS.PATIENT_CREATE_EDIT) ||
        hasPermission(PERMISSIONS.TELETIP_MANAGER) ||
        hasPermission(PERMISSIONS.STUDY_EDIT_METADATA) ||
        hasPermission(PERMISSIONS.STUDY_SHARE) ||
        hasPermission(PERMISSIONS.STUDY_DELETE_OR_RESTORE) ||
        hasPermission(PERMISSIONS.DELETE_STUDY_PERMANENT);

    const canDownload =
        hasPermission(PERMISSIONS.VOICE_RECORD_DOWNLOAD) ||
        hasPermission(PERMISSIONS.STUDY_OPEN_VIEWER);

    const actionRowLabelStyle = {
        width: 96,
        fontSize: 12,
        color: 'var(--text-3)',
    };

    return (
        <>
            <RowMenu text={row.id}>
                <Menu>
                    {hasPermission(PERMISSIONS.STUDY_REQUEST_SET) && (
                        <Menu.Item
                            key={'add-request'}
                            icon={<FileAddOutlined />}
                            onClick={addRequest}
                        >
                            <div>{t('Add Request') + ' (A)'}</div>
                        </Menu.Item>
                    )}
                    {hasPermission(PERMISSIONS.REPORT_ADD_OR_EDIT) && (
                        <Menu.Item
                            key={'add-request-report'}
                            icon={<SnippetsOutlined />}
                            onClick={openReqReportPage}
                        >
                            <div>{t('Request & Reports') + ' (R)'}</div>
                        </Menu.Item>
                    )}
                    {
                        (
                            hasPermission(PERMISSIONS.STUDY_SET_EMERGENCY)
                            || hasPermission(PERMISSIONS.STUDY_REMOVE_EMERGENCY)
                        ) &&
                        (
                            <Select
                                size={'small'}
                                style={{ width: 250, paddingLeft: 4, paddingRight: 4 }}
                                value={parseInt(row.studyType)}
                                disabled={!hasPermission(PERMISSIONS.STUDY_REMOVE_EMERGENCY) && parseInt(row.studyType) === 3}
                                suffixIcon={t('Study Type')}
                                onChange={async (e) => {
                                    await setStudyType(e)
                                }}
                            >
                                <Select.Option value={1}>{t('Routine')}</Select.Option>
                                {
                                    hasPermission(PERMISSIONS.STUDY_SET_EMERGENCY) && (
                                        <Select.Option value={3}>{t('Emergency')}</Select.Option>
                                    )
                                }
                                <Select.Option value={2}>{t('Inpatient')}</Select.Option>
                                <Select.Option value={4}>{t('Comparative Examination')}</Select.Option>
                            </Select>
                        )
                    }

                    {showActionRows && <Menu.Divider key={'action-rows-top'} />}

                    {canToggleRead && (
                        <Menu.Item key={'set-read-menu'}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                }}
                            >
                                <span style={actionRowLabelStyle}>
                                    {t('Read')}:
                                </span>
                                {hasPermission(PERMISSIONS.STUDY_SET_READ) && (
                                    <Button
                                        size={'small'}
                                        style={{ width: 80 }}
                                        onClick={async () => {
                                            await setReadTrue();
                                        }}
                                    >
                                        {t('Read')}
                                    </Button>
                                )}
                                {hasPermission(PERMISSIONS.STUDY_REMOVE_READ) && (
                                    <Button
                                        size={'small'}
                                        style={{ width: 80 }}
                                        onClick={async () => {
                                            await deleteReadUser();
                                        }}
                                    >
                                        {t('Not Read')}
                                    </Button>
                                )}
                            </div>
                        </Menu.Item>
                    )}

                    {canRemoveAssignment && (
                        <Menu.Item key={'remove-assignment'}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                }}
                            >
                                <span style={actionRowLabelStyle}>
                                    {t('Remove Assignment')}:
                                </span>
                                {hasPermission(
                                    PERMISSIONS.STUDY_ASSIGN_DOCTOR_USER
                                ) && (
                                    <Button
                                        size={'small'}
                                        style={{ width: 80 }}
                                        onClick={removeDoctorUser}
                                    >
                                        {t('Doctor')}
                                    </Button>
                                )}
                                {hasPermission(
                                    PERMISSIONS.STUDY_ASSIGN_REPORTER_USER
                                ) && (
                                    <Button
                                        size={'small'}
                                        style={{ width: 80 }}
                                        onClick={removeReporterUser}
                                    >
                                        {t('Reporter Short')}
                                    </Button>
                                )}
                            </div>
                        </Menu.Item>
                    )}

                    {showActionRows && <Menu.Divider key={'action-rows-bottom'} />}

                    {hasPermission(PERMISSIONS.SET_STUDY_REPORTABLE) && !row.isReportable && (
                        <Menu.Item key={'set-reportable'} onClick={setReportable}>
                            <div>{t('Set Reportable')}</div>
                        </Menu.Item>
                    )}
                    {hasPermission(PERMISSIONS.SET_STUDY_NOT_REPORTABLE) && row.isReportable && (
                        <Menu.Item key={'set-not-reportable'} onClick={setNotReportable}>
                            <div>{t('Set Not Reportable')}</div>
                        </Menu.Item>
                    )}
                    {canRemoveAssignment && (
                        <Menu.Item
                            key={'assign-user'}
                            icon={<UserAddOutlined />}
                            onClick={assignUser}
                        >
                            <div>{t('Assign User')}</div>
                        </Menu.Item>
                    )}
                    {canDownload && (
                        <Menu.SubMenu
                            key={'download'}
                            icon={<DownloadOutlined />}
                            title={t('Download')}
                        >
                            {hasPermission(PERMISSIONS.VOICE_RECORD_DOWNLOAD) && (
                                <Menu.Item
                                    key={'download-voice-records'}
                                    icon={<AudioOutlined />}
                                    onClick={downloadVoiceRecords}
                                >
                                    <div>{t('Voice Records')}</div>
                                </Menu.Item>
                            )}
                            {hasPermission(PERMISSIONS.STUDY_OPEN_VIEWER) && (
                                <Menu.Item
                                    key={'download-study-zip'}
                                    onClick={downloadDicomZip}
                                >
                                    <div>Dicom Zip</div>
                                </Menu.Item>
                            )}
                        </Menu.SubMenu>
                    )}

                    {
                        (hasPermission(PERMISSIONS.STUDY_TASK_MANAGER) || hasPermission(PERMISSIONS.STUDY_TASK_CREATE_MISSING_DICOM)) && (
                            <Menu.SubMenu key={'report-issue'} title={t('Report Issue')}>
                                <Menu.Item
                                    key={'report-missing-dicoms'}
                                    onClick={() => {
                                        openModal(
                                            t('Report Missing Dicoms'),
                                            <ReportMissingDicomsModal
                                                studyId={row.id}
                                                onSaved={() => {
                                                    refreshDataGrid();
                                                    closeModal();
                                                }}
                                                onCancel={() => closeModal()}
                                            />
                                        );
                                    }}
                                >
                                    <div>{t('Report Missing Dicoms')}</div>
                                </Menu.Item>
                            </Menu.SubMenu>
                        )
                    }

                    {canOpenStudySubMenu && (
                        <Menu.SubMenu key={'study'} title={t('Study')}>
                            {hasPermission(PERMISSIONS.STUDY_SET_PRIORITY) && (
                                <Menu.Item
                                    key={'set-priority'}
                                    icon={<ThunderboltOutlined />}
                                    onClick={setPriority}
                                >
                                    <div>{t('Set Priority')}</div>
                                </Menu.Item>
                            )}

                            {hasPermission(PERMISSIONS.STUDY_SET_PRIORITY) && (
                                <Menu.Divider key={'study-priority-divider'} />
                            )}

                            {hasPermission(PERMISSIONS.LIST_ITEM_ADD) && (
                                <Menu.Item
                                    key={'add-to-list'}
                                    icon={<StarOutlined />}
                                    onClick={addToList}
                                >
                                    <div>{t('Add to List')}</div>
                                </Menu.Item>
                            )}
                            {hasPermission(PERMISSIONS.PATIENT_CREATE_EDIT) && (
                                <Menu.Item key={'edit-patient'} onClick={editPatient}>
                                    <div>{t('Edit Patient')}</div>
                                </Menu.Item>
                            )}
                            {hasPermission(PERMISSIONS.TELETIP_MANAGER) && (
                                <Menu.Item key={'make-kos'} onClick={makeKos}>
                                    <div>{t('Make Kos')}</div>
                                </Menu.Item>
                            )}
                            {hasPermission(PERMISSIONS.STUDY_EDIT_METADATA) && (
                                <Menu.Item
                                    key={'edit-study-metadata'}
                                    onClick={editStudyMetadata}
                                >
                                    <div>{t('Edit Study Metadata')}</div>
                                </Menu.Item>
                            )}
                            {hasPermission(PERMISSIONS.STUDY_SHARE) && (
                                <Menu.Item
                                    key={'share-email'}
                                    onClick={() => {
                                        openModal(
                                            t('Share Study'),
                                            <StudyShareModal studyIds={[row.id]} />
                                        );
                                    }}
                                >
                                    <div>{t('Share')}</div>
                                </Menu.Item>
                            )}

                            <Menu.Divider key={'study-delete-divider'} />

                            {hasPermission(PERMISSIONS.STUDY_DELETE_OR_RESTORE) &&
                                !row.isDeleted && (
                                    <Menu.Item
                                        key={'delete-study'}
                                        icon={<DeleteOutlined />}
                                        onClick={deleteStudy}
                                    >
                                        <div>{t('Delete Study')}</div>
                                    </Menu.Item>
                                )}
                            {hasPermission(PERMISSIONS.STUDY_DELETE_OR_RESTORE) &&
                                row.isDeleted && (
                                    <Menu.Item
                                        key={'restore-study'}
                                        onClick={restoreStudy}
                                    >
                                        <div>{t('Restore Study')}</div>
                                    </Menu.Item>
                                )}
                            {hasPermission(PERMISSIONS.DELETE_STUDY_PERMANENT) &&
                                !row.isDeleted && (
                                    <Menu.Item
                                        key={'delete-study-permanent'}
                                        icon={<DeleteFilled style={{ color: '#cf1322' }} />}
                                        onClick={deleteStudiesPermanent}
                                    >
                                        <div style={{ color: '#cf1322' }}>
                                            {t('Delete Study Permanent')}
                                        </div>
                                    </Menu.Item>
                                )}
                        </Menu.SubMenu>
                    )}
                </Menu>
            </RowMenu>
        </>
    );
}
